import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  SortAsc,
  SortDesc,
  RefreshCw,
  Download,
  FileText,
  Activity,
  Database,
  BookOpen
} from 'lucide-react';

const GeneTable = () => {
  // State for data
  const [genes, setGenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for pagination
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0
  });

  // State for sorting
  const [sorting, setSorting] = useState({
    sortBy: 'ID',
    sortDir: 'asc'
  });

  // State for filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [consensusOnly, setConsensusOnly] = useState(false);

  // State for consensus info
  const [consensusInfo, setConsensusInfo] = useState({
    totalInResult: 0,
    percentageInResult: 0,
    totalInDatabase: 0
  });

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch data when pagination, sorting, or filtering changes
  useEffect(() => {
    fetchGenes();
  }, [
    pagination.page,
    pagination.pageSize,
    sorting.sortBy,
    sorting.sortDir,
    debouncedSearchTerm,
    consensusOnly
  ]);

  // Function to fetch genes
  const fetchGenes = async () => {
    try {
      setLoading(true);

      // Construct query parameters
      const params = new URLSearchParams({
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy: sorting.sortBy,
        sortDir: sorting.sortDir,
        search: debouncedSearchTerm,
        consensusOnly: consensusOnly
      });

      // Fetch data from API
      const response = await fetch(`/api/gene/table?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setGenes(data.data);
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          totalPages: data.pagination.totalPages
        }));
        setConsensusInfo(data.consensus);
      } else {
        throw new Error(data.message || 'Failed to fetch gene data');
      }

    } catch (err) {
      setError(err.message);
      console.error('Error fetching gene data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle sort change
  const handleSort = (column) => {
    setSorting(prev => ({
      sortBy: column,
      sortDir: prev.sortBy === column && prev.sortDir === 'asc' ? 'desc' : 'asc'
    }));

    // Reset to first page when sorting changes
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({
        ...prev,
        page: newPage
      }));
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  // Handle consensus filter
  const handleConsensusChange = (e) => {
    setConsensusOnly(e.target.checked);
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  // Export as CSV
  const exportToCsv = () => {
    if (genes.length === 0) return;

    // Create CSV header
    const headers = [
      'ID',
      'Human Name',
      'Human Entrez',
      'Mouse Name',
      'Mouse Entrez',
      'Rat Name',
      'Rat Entrez',
      'MGI',
      'Publications',
      'Diseases',
      'OMIM',
      'SynGO',
      'Is Consensus'
    ];

    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...genes.map(gene => [
        gene.id,
        `"${gene.humanName || ''}"`,
        gene.humanEntrez || '',
        `"${gene.mouseName || ''}"`,
        gene.mouseEntrez || '',
        `"${gene.ratName || ''}"`,
        gene.ratEntrez || '',
        `"${gene.mgi || ''}"`,
        gene.paperCount || 0,
        gene.diseaseCount || 0,
        gene.omimCount || 0,
        gene.syngoCount || 0,
        gene.isConsensus ? 'Yes' : 'No'
      ].join(','))
    ];

    // Create and download CSV file
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', 'gene_table.csv');
    link.click();

    URL.revokeObjectURL(url);
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sorting.sortBy !== column) {
      return <SortAsc className="ml-1 h-4 w-4 text-gray-400" />;
    }

    return sorting.sortDir === 'asc'
      ? <SortAsc className="ml-1 h-4 w-4" />
      : <SortDesc className="ml-1 h-4 w-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            Synapse Proteome Catalog
          </h1>
          <div className="flex flex-col space-y-2 md:flex-row md:space-x-2 md:space-y-0">
            <div className="flex items-center relative">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-2 pr-8 py-1 border border-gray-300 rounded-md text-sm max-w-xs"
              />
              <Search className="absolute right-2 h-4 w-4 text-gray-500" />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="consensus"
                checked={consensusOnly}
                onChange={handleConsensusChange}
                className="rounded text-blue-600"
              />
              <label
                htmlFor="consensus"
                className="text-sm font-medium leading-none"
              >
                Consensus Only
              </label>
            </div>

            <button
              onClick={() => fetchGenes()}
              className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>

            <button
              onClick={exportToCsv}
              className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={genes.length === 0}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Content */}
        {error ? (
          <div className="rounded-md bg-red-50 p-4 my-4">
            <div className="flex">
              <div className="text-red-700">
                <p className="text-sm">Error loading gene data: {error}</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {consensusInfo.totalInDatabase > 0 && (
              <div className="rounded-md bg-blue-50 p-4 mb-4">
                <div className="flex">
                  <div className="text-blue-700">
                    <p className="text-sm">
                      <strong>Consensus Genes:</strong> {consensusInfo.totalInResult} of {genes.length} genes shown ({consensusInfo.percentageInResult}%) -
                      Total of {consensusInfo.totalInDatabase} consensus genes in database
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white shadow rounded-lg">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer w-32"
                        onClick={() => handleSort('HumanName')}
                      >
                        <div className="flex items-center">
                          Gene {renderSortIcon('HumanName')}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer w-24"
                        onClick={() => handleSort('HumanEntrez')}
                      >
                        <div className="flex items-center">
                          Human Entrez {renderSortIcon('HumanEntrez')}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer w-24"
                        onClick={() => handleSort('MouseEntrez')}
                      >
                        <div className="flex items-center">
                          Mouse Entrez {renderSortIcon('MouseEntrez')}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer w-20"
                        onClick={() => handleSort('RatEntrez')}
                      >
                        <div className="flex items-center">
                          Rat Entrez {renderSortIcon('RatEntrez')}
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer w-20"
                        onClick={() => handleSort('MGI')}
                      >
                        <div className="flex items-center">
                          MGI {renderSortIcon('MGI')}
                        </div>
                      </th>

                      {/* Count columns with compact icons */}
                      <th
                        scope="col"
                        className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16"
                        title="Number of Publications"
                      >
                        <div className="flex flex-col items-center">
                          <FileText className="h-4 w-4 mb-1" />
                          <span className="text-2xs">Publications</span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16"
                        title="Number of Diseases"
                      >
                        <div className="flex flex-col items-center">
                          <Activity className="h-4 w-4 mb-1" />
                          <span className="text-2xs">Diseases</span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16"
                        title="Number of OMIM Records"
                      >
                        <div className="flex flex-col items-center">
                          <Database className="h-4 w-4 mb-1" />
                          <span className="text-2xs">OMIM</span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16"
                        title="Number of SynGO Records"
                      >
                        <div className="flex flex-col items-center">
                          <BookOpen className="h-4 w-4 mb-1" />
                          <span className="text-2xs">SynGO</span>
                        </div>
                      </th>

                      <th
                        scope="col"
                        className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20"
                      >
                        <span className="text-2xs">Consensus</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={14} className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center py-6">
                            <RefreshCw className="h-6 w-6 animate-spin text-gray-500" />
                            <span className="ml-2">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : genes.length === 0 ? (
                      <tr>
                        <td colSpan={14} className="px-6 py-4 whitespace-nowrap text-center py-6 text-gray-500">
                          No gene records found
                        </td>
                      </tr>
                    ) : (
                      genes.map(gene => (
                        <tr key={gene.id} className={gene.isConsensus ? 'bg-blue-50' : ''}>
                          <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 max-w-xs">
                            {gene.humanName ? (
                              <a
                                href={`/gene-search?q=${encodeURIComponent(gene.humanName)}`}
                                className="block truncate text-gray-600 hover:text-blue-600 transition-colors duration-200"
                              >
                                {gene.humanName}
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 max-w-xs">
                            {gene.humanEntrez ? (
                              <a
                                href={`https://www.ncbi.nlm.nih.gov/gene/${encodeURIComponent(gene.humanEntrez)}`}
                                className="block truncate text-gray-600 hover:text-blue-600 transition-colors duration-200"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {gene.humanEntrez}
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 max-w-xs">
                            {gene.mouseEntrez ? (
                              <a
                                href={`https://www.ncbi.nlm.nih.gov/gene/${encodeURIComponent(gene.mouseEntrez)}`}
                                className="block truncate text-gray-600 hover:text-blue-600 transition-colors duration-200"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {gene.mouseEntrez}
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 max-w-xs">
                            {gene.ratEntrez ? (
                              <a
                                href={`https://www.ncbi.nlm.nih.gov/gene/${encodeURIComponent(gene.ratEntrez)}`}
                                className="block truncate text-gray-600 hover:text-blue-600 transition-colors duration-200"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {gene.ratEntrez}
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 max-w-xs">
                            {gene.mgi ? (
                              <a
                                href={`https://www.informatics.jax.org/marker/${encodeURIComponent(gene.mgi)}`}
                                className="block truncate text-gray-600 hover:text-blue-600 transition-colors duration-200"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {gene.mgi}
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>

                          {/* Count cells with compact badges */}
                          <td className="px-1 py-2 whitespace-nowrap text-sm text-center">
                            <a href={`/gene-search?q=${encodeURIComponent(gene.humanName)}`} className="block">
                              {gene.paperCount > 0 ? (
                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                  {gene.paperCount}
                                </span>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </a>
                          </td>

                          <td className="px-1 py-2 whitespace-nowrap text-sm text-center">
                            <a href={`/gene-search?q=${encodeURIComponent(gene.humanName)}`} className="block">
                              {gene.diseaseCount > 0 ? (
                                <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                                  {gene.diseaseCount}
                                </span>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </a>
                          </td>

                          <td className="px-1 py-2 whitespace-nowrap text-sm text-center">
                            <a href={`/gene-search?q=${encodeURIComponent(gene.humanName)}`} className="block">
                              {gene.omimCount > 0 ? (
                                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                  {gene.omimCount}
                                </span>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </a>
                          </td>

                          <td className="px-1 py-2 whitespace-nowrap text-sm text-center">
                            <a href={`/gene-search?q=${encodeURIComponent(gene.humanName)}`} className="block">
                              {gene.syngoCount > 0 ? (
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                  {gene.syngoCount}
                                </span>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </a>
                          </td>

                          <td className="px-1 py-2 whitespace-nowrap text-sm text-center">
                            <a href={`/gene-search?q=${encodeURIComponent(gene.humanName)}`} className="block">
                              {gene.isConsensus ? (
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                  Yes
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                                  No
                                </span>
                              )}
                            </a>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-500">
                Showing {genes.length > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0}-
                {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{" "}
                {pagination.total} results
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1 || loading}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <span className="text-sm">
                  Page {pagination.page} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages || loading}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.page === pagination.totalPages || loading}
                  className="inline-flex items-center px-2 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GeneTable;